import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/SavingsChart.css';

interface SavingsData {
  month: string;
  value: number;
}

interface CompanySavings {
  total_savings: number;
  current_month_savings: number;
  previous_month_savings: number;
  month_over_month_change: number;
  current_month: number;
  current_year: number;
  previous_month: number;
  previous_year: number;
  currency?: string;
  [key: string]: any; // Allow dynamic savings keys like savings_2025_02
}

interface SavingsChartProps {
  data?: SavingsData[];
  title?: string;
  height?: number;
  maxValue?: number;
}

const SavingsChart: React.FC<SavingsChartProps> = ({ 
  data,
  title,
  height = 300,
  maxValue
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedCurrency, isLoading, currencySymbol } = useCurrency();
  const [savings, setSavings] = useState<CompanySavings | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Fetch real company savings data
  useEffect(() => {
    const fetchCompanySavings = async () => {
      try {
        if (!user?.company?._id) return;

        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) return;

        console.log('🔍 SavingsChart - Fetching savings for company:', user.company._id);

        const response = await fetch(`${API_ENDPOINTS.COMPANY_SAVINGS(user.company._id)}?currency=${selectedCurrency}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('🔍 SavingsChart - Backend response:', result);
          
          if (result.success && result.data) {
            console.log('🔍 SavingsChart - Setting savings data:', result.data);
            setSavings(result.data);
          }
        } else {
          console.error('🔍 SavingsChart - Backend error:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error fetching company savings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanySavings();
  }, [user?.company?._id, selectedCurrency, isLoading]);

  // Generate real savings data from company data
  const generateRealSavingsData = (): SavingsData[] => {
    if (!savings) return [];
    
    const currentYear = savings.current_year;
    const currentMonth = savings.current_month;
    
    // Generate data for the last 12 months
    const data: SavingsData[] = [];
    
    for (let i = 11; i >= 0; i--) {
      let targetMonth = currentMonth - i;
      let targetYear = currentYear;
      
      if (targetMonth <= 0) {
        targetMonth += 12;
        targetYear -= 1;
      }
      
      const monthKey = `savings_${targetYear}_${targetMonth.toString().padStart(2, '0')}`;
      const monthName = getMonthName(targetMonth);
      
      // Try to get savings data from the savings object
      let monthValue = 0;
      
      // Check if we have savings data for this month
      if (savings[monthKey]) {
        monthValue = savings[monthKey];
      } else if (targetMonth === currentMonth && targetYear === currentYear) {
        monthValue = savings.current_month_savings;
      } else if (targetMonth === savings.previous_month && targetYear === savings.previous_year) {
        monthValue = savings.previous_month_savings;
      }
      
      data.push({
        month: monthName,
        value: monthValue
      });
    }
    
    return data;
  };

  // Get month name from month number
  const getMonthName = (month: number): string => {
    const months = [
      t('common.jan'), t('common.feb'), t('common.mar'), t('common.apr'),
      t('common.may'), t('common.jun'), t('common.jul'), t('common.aug'),
      t('common.sep'), t('common.oct'), t('common.nov'), t('common.dec')
    ];
    return months[month - 1] || month.toString();
  };

  // Default data with translations (fallback)
  const [defaultData, setDefaultData] = React.useState([
    { month: 'Jan', value: 1000 },
    { month: 'Feb', value: 5000 },
    { month: 'Mar', value: 8000 },
    { month: 'Apr', value: 11500 },
    { month: 'May', value: 13500 },
    { month: 'Jun', value: 12500 },
    { month: 'Jul', value: 15500 },
    { month: 'Aug', value: 19000 },
    { month: 'Sep', value: 17500 },
    { month: 'Oct', value: 16000 },
    { month: 'Nov', value: 18500 },
    { month: 'Dec', value: 19000 }
  ]);
  
  React.useEffect(() => {
    setDefaultData([
      { month: t('common.jan'), value: 1000 },
      { month: t('common.feb'), value: 5000 },
      { month: t('common.mar'), value: 8000 },
      { month: t('common.apr'), value: 11500 },
      { month: t('common.may'), value: 13500 },
      { month: t('common.jun'), value: 12500 },
      { month: t('common.jul'), value: 15500 },
      { month: t('common.aug'), value: 19000 },
      { month: t('common.sep'), value: 17500 },
      { month: t('common.oct'), value: 16000 },
      { month: t('common.nov'), value: 18500 },
      { month: t('common.dec'), value: 19000 }
    ]);
  }, [t]);
  
  // Use real data if available, otherwise fallback to default
  const chartData = data || (savings ? generateRealSavingsData() : defaultData);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  
  // Calculate max value dynamically: highest value * 2, divided into 5 equal parts
  const highestValue = Math.max(...chartData.map(item => item.value));
  const calculatedMaxValue = maxValue || (highestValue > 0 ? highestValue * 2 : 1000);
  const yAxisSteps = 5; // Changed from 6 to 5
  const yAxisValues = Array.from({ length: yAxisSteps }, (_, i) => 
    Math.round((calculatedMaxValue / (yAxisSteps - 1)) * i)
  );

  console.log('🔍 SavingsChart - Chart data:', chartData);
  console.log('🔍 SavingsChart - Highest value:', highestValue);
  console.log('🔍 SavingsChart - Calculated max value:', calculatedMaxValue);
  console.log('🔍 SavingsChart - Y-axis values:', yAxisValues);

  // Calculate SVG path for the line
  const getLinePath = () => {
    if (chartData.length < 2) return '';
    
    const width = 100;
    const height = 100;
    const padding = 4; // Add padding to prevent points from touching edges
    const chartWidth = width - (padding * 2);
    const xStep = chartWidth / (chartData.length - 1);
    
    const points = chartData.map((item, index) => {
      const x = padding + (index * xStep);
      const y = height - ((item.value / calculatedMaxValue) * height);
      return `${x},${y}`;
    });
    
    return `M ${points.join(' L ')}`;
  };

  // Calculate point positions
  const getPointPosition = (index: number, value: number) => {
    const width = 100;
    const height = 100;
    const padding = 4; // Add padding to prevent points from touching edges
    const chartWidth = width - (padding * 2);
    const xStep = chartWidth / (chartData.length - 1);
    const x = padding + (index * xStep);
    const y = height - ((value / calculatedMaxValue) * height);
    return { x, y };
  };

  // Check if tooltip should appear above or below the point
  const shouldShowTooltipAbove = (index: number) => {
    const pos = getPointPosition(index, chartData[index].value);
    return pos.y > 50; // If point is in lower half, show tooltip above
  };

  // Show loading when currency is changing or data is loading
  if (isLoading || loading) {
    return (
      <div className="savings-chart">
        <h3 className="chart-title">{title || t('common.savings')}</h3>
        <div className="savings-chart-loading">
          <div className="loading-skeleton"></div>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="savings-chart">
      <h3 className="chart-title">{title || t('common.savings')}</h3>
      <div className="chart-wrapper">
        {/* Y-axis */}
        <div className="y-axis">
          {yAxisValues.reverse().map((value) => (
            <div key={value} className="y-axis-label">
              {currencySymbol}{value.toLocaleString()}
            </div>
          ))}
        </div>

        {/* Main chart area */}
        <div className="chart-main">
          {/* Grid lines */}
          <div className="grid-lines">
            {yAxisValues.map((value) => (
              <div 
                key={value} 
                className="grid-line"
                style={{ 
                  bottom: `${(value / calculatedMaxValue) * 100}%` 
                }}
              />
            ))}
          </div>

          {/* SVG Chart */}
          <div className="svg-container">
            <svg 
              viewBox="0 0 100 100" 
              preserveAspectRatio="none"
              className="chart-svg"
            >
              {/* Line */}
              <path
                d={getLinePath()}
                stroke="#6B7280"
                strokeWidth="0.5"
                fill="none"
                className="line-path"
              />
              
              {/* Points */}
              {chartData.map((item, index) => {
                const pos = getPointPosition(index, item.value);
                return (
                  <g key={index}>
                    {/* Main point */}
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="1"
                      fill={hoveredPoint === index ? "#111827" : "#6B7280"}
                      className="data-point"
                    />
                    
                    {/* Hover circle (larger, transparent) - on top */}
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="6"
                      fill="transparent"
                      className="hover-circle"
                      onMouseEnter={() => setHoveredPoint(index)}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                  </g>
                );
              })}
              
              {/* Tooltip inside SVG */}
              {hoveredPoint !== null && (
                <g className="svg-tooltip">
                  {shouldShowTooltipAbove(hoveredPoint) ? (
                    // Tooltip above the point
                    <>
                      <text
                        x={getPointPosition(hoveredPoint, chartData[hoveredPoint].value).x}
                        y={getPointPosition(hoveredPoint, chartData[hoveredPoint].value).y - 8}
                        textAnchor="middle"
                        fill="#6b7280"
                        fontSize="2.5"
                        fontWeight="400"
                        className="tooltip-month"
                      >
                        {chartData[hoveredPoint].month} {currencySymbol}{chartData[hoveredPoint].value.toLocaleString()}
                      </text>
                    </>
                  ) : (
                    // Tooltip below the point
                    <>
                      <text
                        x={getPointPosition(hoveredPoint, chartData[hoveredPoint].value).x}
                        y={getPointPosition(hoveredPoint, chartData[hoveredPoint].value).y + 12}
                        textAnchor="middle"
                        fill="#6b7280"
                        fontSize="2.5"
                        fontWeight="400"
                        className="tooltip-month"
                      >
                        {chartData[hoveredPoint].month} {currencySymbol}{chartData[hoveredPoint].value.toLocaleString()}
                      </text>
                    </>
                  )}
                </g>
              )}
            </svg>
          </div>
        </div>
      </div>
      
      {/* Month labels */}
      <div className="labels-wrapper">
        {chartData.map((item, index) => (
          <div key={index} className="label-item">
            {item.month}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavingsChart; 